<!--
  =====================================================================
  INFORMATIVE AL PUBBLICO PAGE - PUBLIC INFORMATION
  =====================================================================
  
  Displays regulatory documents and informative content for the public.
  
  FEATURES:
  - Loads public information documents from store
  - Filters sections to show only informative-grid layouts
  - Shows page header with eyebrow and title
  - Responsive layout with proper spacing
  - Shows loading and error states
  
  RENDERED COMPONENTS:
  - SectionInformativeGrid: Grid display of informative documents
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="pagina">
      <div v-if="state.loading" class="loading-state">
        <div class="dots-loader">
          <span class="dot dot-1"></span>
          <span class="dot dot-2"></span>
          <span class="dot dot-3"></span>
        </div>
      </div>
      
      <div v-else-if="state.error" class="error-state">
        Errore durante il caricamento delle informative
      </div>
      
      <div v-else>
        <div class="page-header">
          <p class="page-header__eyebrow">Documenti e normative</p>
          <h1 class="page-header__title">{{ state.page.title }}</h1>
        </div>

        <component
          v-for="(section, index) in filteredSections"
          :key="index"
          :is="getComponent(section.type)"
          :content="section.content"
        />
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { state, loadPageContent } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// INFORMATIVE-SPECIFIC COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionInformativeGrid from '@/components/sections/informative-al-pubblico/SectionInformativeGrid.vue'

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Filters page sections to show only informative-grid layouts
 * 
 * @returns {Array} Array of informative grid sections
 */
const filteredSections = computed(() => {
  return state.pageSections.filter(section => section.type === 'informative-grid')
})

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Load informative content on component mount
 */
onMounted(async () => {
  await loadPageContent('/informative-al-pubblico')
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps section types to their corresponding Vue components
 * 
 * @param {string} type - The section type from page content
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'informative-grid': 
      return SectionInformativeGrid
    default: 
      return SectionInformativeGrid
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main';

/* ------------------------------------------------------------------
   Main page container
------------------------------------------------------------------ */
.pagina {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  @include flex-gap($space-2, column);

  @media (max-width: 768px) {
    padding: 1rem;
    @include flex-gap($space-1-5, column);
  }
}

/* ------------------------------------------------------------------
   Loading and error states
------------------------------------------------------------------ */
.loading-state,
.error-state {
  text-align: center;
  padding: $space-3 $space-1;
  color: $clr-text-light;
  font-size: $font-base;

  @media (max-width: 768px) {
    padding: $space-2 $space-0-5;
    font-size: $font-base-sm;
  }
}

/* ------------------------------------------------------------------
   Page header with eyebrow and title
   Aligned with .section-header styles
------------------------------------------------------------------ */
.page-header {
  margin-bottom: clamp(2rem, 4vw, 3rem);

  @media (max-width: 768px) {
    margin-bottom: $space-2;
  }

  @media (max-width: 480px) {
    margin-bottom: $space-1-5;
  }

  &__eyebrow {
    position: relative;
    display: inline-block;
    font-size: $font-eyebrow;
    font-weight: 600;
    text-transform: uppercase;
    color: $clr-tertiary;
    margin-left: 60px;
    margin-bottom: $space-2;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -100px;
      width: 40%;
      height: 12px;
      background-color: rgba($clr-tertiary, 0.7);
      transition: all 0.3s ease;
    }

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;

        &::before {
          background-color: rgba($clr-tertiary, 0.8);
          width: 140%;
          transform: scaleY(1.2);
        }
      }
    }

    @media (max-width: 480px) {
      font-size: $font-eyebrow-xs;
      margin-left: 28px;
      margin-bottom: $space-0-25;

      &::before {
        height: 7px;
        left: -30px;
        width: 40%;
      }
    }

    @media (max-width: 360px) {
      font-size: $font-eyebrow-xxs;
      margin-left: 23px;
    }
  }

  &__title {
    font-size: $font-title;
    font-weight: 300;
    color: $text-color;
    margin: 0;
    margin-left: 60px;
    margin-bottom: $space-2;

    @media (max-width: 1300px) {
      font-size: $font-title-lg;
    }

    @media (max-width: 920px) {
      font-size: $font-title-sm;
    }

    @media (max-width: 480px) {
      font-size: $font-title-xs;
      margin-left: 28px;
      margin-bottom: $space-0-5;
    }

    @media (max-width: 360px) {
      font-size: $font-title-xxs;
      margin-left: 23px;
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   Fascia 481-920px: l'header aveva margin-left 60px su un contenitore
   che parte da 16/64, quindi finiva a 76/124 invece dei 54/102 del
   riferimento "chi siamo". I blocchi 480/360 sono ripetuti qui sotto
   perche' stando in fondo al file l'ordine decide: senza, la regola
   dei 920 vincerebbe anche a 390px.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 920px) {
  .page-header__eyebrow.page-header__eyebrow.page-header__eyebrow,
  .page-header__title.page-header__title.page-header__title {
    margin-left: 38px;
  }
}

@media (max-width: 480px) {
  .page-header__eyebrow.page-header__eyebrow.page-header__eyebrow,
  .page-header__title.page-header__title.page-header__title {
    margin-left: 28px;
  }
}

@media (max-width: 360px) {
  .page-header__eyebrow.page-header__eyebrow.page-header__eyebrow,
  .page-header__title.page-header__title.page-header__title {
    margin-left: 23px;
  }
}
</style>
